import {
	AlignmentType,
	BorderStyle,
	Document,
	Packer,
	Paragraph,
	ShadingType,
	Table,
	TableCell,
	TableRow,
	TextRun,
	WidthType,
} from "docx";

/** Matches app body font from common.scss */
const UI_FONT = "Inter";

const COLOR = {
	body: "374151",
	heading: "111827",
	muted: "6B7280",
	removed: "DC3545",
};

const DIFF_ADDED_FILL = "D1E7DD";
const DIFF_REMOVED_FILL = "F8D7DA";
const METADATA_VALUE_FILL = "F9FAFB";
const METADATA_BORDER_COLOR = "E5E7EB";
const METADATA_FIELD_BORDER = {
	top: { style: BorderStyle.SINGLE, size: 1, color: METADATA_BORDER_COLOR },
	bottom: { style: BorderStyle.SINGLE, size: 1, color: METADATA_BORDER_COLOR },
	left: { style: BorderStyle.SINGLE, size: 1, color: METADATA_BORDER_COLOR },
	right: { style: BorderStyle.SINGLE, size: 1, color: METADATA_BORDER_COLOR },
};

/** mb-3 ≈ 16px paragraph gap; line-height 1.6 */
const BODY_PARAGRAPH_SPACING = { after: 240, line: 384, lineRule: "auto" };
/** mb-2 between diff blocks */
const DIFF_BLOCK_GAP = { before: 160, after: 160 };
/** Section label spacing */
const SECTION_SPACING = { after: 60 };
/** Field group spacing */
const FIELD_GROUP_SPACING = { after: 80 };
/** Metadata field spacing — matches UI gap-3 (twips; 20 twips ≈ 1px) */
const METADATA_FIELD_GAP = 240;
const METADATA_HINT_AFTER = 40;
const METADATA_LINE = { line: 240, lineRule: "exact" };

const TYPOGRAPHY = {
	body: { size: 24, color: COLOR.body },
	bodyStrong: { size: 24, color: COLOR.heading, bold: true },
	sectionLabel: { size: 21, color: COLOR.muted, bold: true },
	fieldLabel: { size: 23, color: COLOR.heading, bold: true },
	fieldHint: { size: 21, color: COLOR.muted },
	currentMeta: { size: 21, color: COLOR.muted, italic: true },
	createH1: { size: 36, color: COLOR.heading, bold: true },
	previewH1: { size: 36, color: COLOR.heading, bold: true },
	previewH2: { size: 30, color: COLOR.heading, bold: true },
	previewH3: { size: 27, color: COLOR.heading, bold: true },
	previewH4: { size: 27, color: COLOR.heading, bold: true },
	optimizeH1: { size: 30, color: COLOR.heading, bold: true },
	optimizeH2: { size: 24, color: COLOR.heading, bold: true },
	optimizeH3: { size: 24, color: COLOR.heading, bold: true },
	optimizeH4: { size: 24, color: COLOR.heading, bold: true },
	listItem: { size: 24, color: COLOR.body },
	pageTitle: { size: 32, color: COLOR.heading, bold: true },
	modeCreate: { size: 21, color: "047857", bold: true },
	modeOptimize: { size: 21, color: "2563EB", bold: true },
};

function escapeRegExp(value) {
	return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripImagesFromMarkdown(markdown) {
	return String(markdown || "")
		.replace(/!\[[^\]]*\]\([^)]*\)/g, "")
		.replace(/<img\b[^>]*\/?>/gi, "")
		.replace(/^\s*$/gm, "")
		.replace(/\n{3,}/g, "\n\n")
		.trim();
}

function stripDuplicateLeadingH1(markdown, h1) {
	const trimmedH1 = String(h1 || "").trim();
	if (!trimmedH1 || !markdown) return stripImagesFromMarkdown(markdown);

	const lines = String(markdown).replace(/\r\n/g, "\n").split("\n");
	let index = 0;
	while (index < lines.length && !lines[index].trim()) index += 1;
	if (index >= lines.length) return stripImagesFromMarkdown(markdown);

	const line = lines[index].trim();
	const pattern = new RegExp(`^#\\s+${escapeRegExp(trimmedH1)}\\s*$`, "i");
	if (!pattern.test(line)) return stripImagesFromMarkdown(markdown);

	lines.splice(index, 1);
	while (index < lines.length && !lines[index].trim()) lines.splice(index, 1);
	return stripImagesFromMarkdown(lines.join("\n"));
}

function sanitizeFilename(name) {
	const sanitized = String(name || "writing-draft")
		.replace(/[<>:"/\\|?*\u0000-\u001F]/g, "")
		.replace(/\s+/g, "-")
		.replace(/-+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 80);
	return sanitized || "writing-draft";
}

function buildDocxFilename(run, payload) {
	const base =
		payload.url_slug ||
		payload.h1 ||
		run?.recommendation_title ||
		run?.recommendation_ref ||
		`writing-draft-${run?.id ?? "export"}`;
	return `${sanitizeFilename(base)}.docx`;
}

function isTableRow(line) {
	const value = String(line || "").trim();
	return value.startsWith("|") && value.endsWith("|") && value.split("|").length > 2;
}

function parseTableCells(line) {
	return String(line || "")
		.trim()
		.slice(1, -1)
		.split("|")
		.map((cell) => cell.trim());
}

function isImageOnlyLine(line) {
	const trimmed = String(line || "").trim();
	return /^!\[[^\]]*\]\([^)]*\)\s*$/.test(trimmed) || /^<img\b[^>]*\/?>\s*$/i.test(trimmed);
}

function currentMetaLabel(value, emptyLabel) {
	if (Array.isArray(value)) {
		const items = value.map((item) => String(item ?? "").trim()).filter(Boolean);
		return items.length ? items.join(", ") : emptyLabel;
	}
	const text = String(value ?? "").trim();
	return text || emptyLabel;
}

function resolveCurrentMetadata(content, ownPage) {
	return {
		title: currentMetaLabel(
			content?.original_meta_title ?? ownPage?.title,
			"No meta title found"
		),
		h1: currentMetaLabel(content?.original_h1 ?? ownPage?.h1, "No H1 found"),
		description: currentMetaLabel(
			content?.original_meta_description ?? ownPage?.meta_description,
			"No meta description found"
		),
		keywords: currentMetaLabel(
			content?.original_meta_keywords ?? ownPage?.meta_keywords,
			"No meta keywords found"
		),
	};
}

function segmentShading(segmentType) {
	if (segmentType === "added") {
		return { type: ShadingType.CLEAR, fill: DIFF_ADDED_FILL };
	}
	if (segmentType === "removed") {
		return { type: ShadingType.CLEAR, fill: DIFF_REMOVED_FILL };
	}
	return undefined;
}

function headingTypography(level, contentStyle) {
	if (contentStyle === "create") {
		if (level <= 1) return TYPOGRAPHY.previewH1;
		if (level === 2) return TYPOGRAPHY.previewH2;
		if (level === 3) return TYPOGRAPHY.previewH3;
		return TYPOGRAPHY.previewH4;
	}
	if (level <= 1) return TYPOGRAPHY.optimizeH1;
	if (level === 2) return TYPOGRAPHY.optimizeH2;
	if (level === 3) return TYPOGRAPHY.optimizeH3;
	return TYPOGRAPHY.optimizeH4;
}

function makeRun(text, typography = TYPOGRAPHY.body, segmentType = "unchanged") {
	const style = typography || TYPOGRAPHY.body;
	const removed = segmentType === "removed";
	return new TextRun({
		text: String(text ?? ""),
		font: UI_FONT,
		size: style.size,
		bold: style.bold || false,
		italics: style.italic || false,
		color: removed ? COLOR.removed : style.color,
		strike: removed,
		underline: style.underline ? {} : undefined,
	});
}

function parseInlineMarkdown(text, typography = TYPOGRAPHY.body, segmentType = "unchanged") {
	const baseStyle = typography || TYPOGRAPHY.body;
	const value = String(text || "");
	if (!value) return [makeRun("", baseStyle, segmentType)];

	const pattern =
		/(\*\*[^*]+\*\*|\*[^*]+\*|__[^_]+__|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
	const runs = [];
	let lastIndex = 0;
	let match;

	while ((match = pattern.exec(value)) !== null) {
		if (match.index > lastIndex) {
			runs.push(makeRun(value.slice(lastIndex, match.index), baseStyle, segmentType));
		}
		const token = match[0];
		if (token.startsWith("**") && token.endsWith("**")) {
			runs.push(
				makeRun(token.slice(2, -2), { ...baseStyle, bold: true }, segmentType)
			);
		} else if (token.startsWith("*") && token.endsWith("*")) {
			runs.push(
				makeRun(token.slice(1, -1), { ...baseStyle, italic: true }, segmentType)
			);
		} else if (token.startsWith("__") && token.endsWith("__")) {
			runs.push(
				makeRun(token.slice(2, -2), { ...baseStyle, bold: true }, segmentType)
			);
		} else if (token.startsWith("_") && token.endsWith("_")) {
			runs.push(
				makeRun(token.slice(1, -1), { ...baseStyle, italic: true }, segmentType)
			);
		} else if (token.startsWith("`") && token.endsWith("`")) {
			runs.push(makeRun(token.slice(1, -1), baseStyle, segmentType));
		} else if (token.startsWith("[")) {
			const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
			if (linkMatch) {
				runs.push(
					makeRun(
						linkMatch[1],
						{ ...baseStyle, underline: true },
						segmentType
					)
				);
			} else {
				runs.push(makeRun(token, baseStyle, segmentType));
			}
		} else {
			runs.push(makeRun(token, baseStyle, segmentType));
		}
		lastIndex = pattern.lastIndex;
	}

	if (lastIndex < value.length) {
		runs.push(makeRun(value.slice(lastIndex), baseStyle, segmentType));
	}

	return runs.length ? runs : [makeRun("", baseStyle, segmentType)];
}

function buildParagraph({
	text,
	typography = TYPOGRAPHY.body,
	segmentType = "unchanged",
	spacing = BODY_PARAGRAPH_SPACING,
	shading,
	indent,
	bullet,
	numbering,
}) {
	return new Paragraph({
		children: parseInlineMarkdown(text, typography, segmentType),
		spacing,
		...(shading ? { shading } : {}),
		...(indent ? { indent } : {}),
		...(bullet ? { bullet } : {}),
		...(numbering ? { numbering } : {}),
	});
}

function markdownToDocxBlocks(markdown, options = {}) {
	const {
		segmentType = "unchanged",
		contentStyle = "optimize",
		blockSpacing = null,
	} = options;

	const cleaned = stripImagesFromMarkdown(markdown);
	const lines = cleaned.replace(/\r\n/g, "\n").split("\n");
	const blocks = [];
	let index = 0;
	let isFirstBlock = true;
	const shading = segmentShading(segmentType);

	function nextParagraphSpacing(overrides = {}) {
		const spacing = {
			...BODY_PARAGRAPH_SPACING,
			...overrides,
		};
		if (isFirstBlock) {
			if (blockSpacing?.before) {
				spacing.before = (spacing.before || 0) + blockSpacing.before;
			}
			isFirstBlock = false;
		}
		return spacing;
	}

	while (index < lines.length) {
		const rawLine = lines[index];
		const line = rawLine.trim();

		if (!line || isImageOnlyLine(line)) {
			index += 1;
			continue;
		}

		if (isTableRow(rawLine)) {
			const tableLines = [];
			while (index < lines.length && isTableRow(lines[index])) {
				tableLines.push(lines[index]);
				index += 1;
			}
			const dataLines = tableLines.filter(
				(row) => !/^\|\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|$/.test(row.trim())
			);
			if (dataLines.length === 0) continue;

			blocks.push(
				new Table({
					width: { size: 100, type: WidthType.PERCENTAGE },
					rows: dataLines.map(
						(row) =>
							new TableRow({
								children: parseTableCells(row).map((cell) =>
									new TableCell({
										...(shading ? { shading } : {}),
										children: [
											buildParagraph({
												text: cell,
												segmentType,
												spacing: { after: 120, line: 384, lineRule: "auto" },
												shading,
											}),
										],
									})
								),
							})
					),
				})
			);
			blocks.push(
				new Paragraph({
					children: [makeRun("")],
					spacing: { after: 240 },
				})
			);
			continue;
		}

		const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
		if (headingMatch) {
			const level = headingMatch[1].length;
			const headingType = headingTypography(level, contentStyle);
			blocks.push(
				buildParagraph({
					text: headingMatch[2],
					typography: headingType,
					segmentType,
					spacing: nextParagraphSpacing({
						before: level <= 2 ? 320 : 240,
						after: 160,
						line: 384,
						lineRule: "auto",
					}),
					shading,
				})
			);
			index += 1;
			continue;
		}

		if (/^(-|\*|\+)\s+/.test(line)) {
			const items = [];
			while (index < lines.length && /^(-|\*|\+)\s+/.test(lines[index].trim())) {
				const item = lines[index].trim().replace(/^(-|\*|\+)\s+/, "");
				if (!isImageOnlyLine(item)) items.push(item);
				index += 1;
			}
			items.forEach((item, itemIndex) => {
				blocks.push(
					buildParagraph({
						text: item,
						typography: TYPOGRAPHY.listItem,
						segmentType,
						spacing: nextParagraphSpacing({
							after: itemIndex === items.length - 1 ? 240 : 90,
							line: 384,
							lineRule: "auto",
						}),
						shading,
						bullet: { level: 0 },
					})
				);
			});
			continue;
		}

		if (/^\d+\.\s+/.test(line)) {
			const items = [];
			while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
				const item = lines[index].trim().replace(/^\d+\.\s+/, "");
				if (!isImageOnlyLine(item)) items.push(item);
				index += 1;
			}
			items.forEach((item, itemIndex) => {
				blocks.push(
					buildParagraph({
						text: item,
						typography: TYPOGRAPHY.listItem,
						segmentType,
						spacing: nextParagraphSpacing({
							after: itemIndex === items.length - 1 ? 240 : 90,
							line: 384,
							lineRule: "auto",
						}),
						shading,
						numbering: { reference: "default-numbering", level: 0 },
					})
				);
			});
			continue;
		}

		if (line.startsWith(">")) {
			const quoteLines = [];
			while (index < lines.length && lines[index].trim().startsWith(">")) {
				quoteLines.push(lines[index].trim().replace(/^>\s?/, ""));
				index += 1;
			}
			blocks.push(
				buildParagraph({
					text: quoteLines.join(" "),
					typography: { ...TYPOGRAPHY.body, color: COLOR.muted },
					segmentType,
					spacing: nextParagraphSpacing(),
					shading,
					indent: { left: 720 },
				})
			);
			continue;
		}

		const paragraphLines = [line];
		index += 1;
		while (
			index < lines.length &&
			lines[index].trim() &&
			!isImageOnlyLine(lines[index]) &&
			!isTableRow(lines[index]) &&
			!/^(#{1,6})\s+/.test(lines[index].trim()) &&
			!/^(-|\*|\+)\s+/.test(lines[index].trim()) &&
			!/^\d+\.\s+/.test(lines[index].trim()) &&
			!lines[index].trim().startsWith(">")
		) {
			paragraphLines.push(lines[index].trim());
			index += 1;
		}

		blocks.push(
			buildParagraph({
				text: paragraphLines.join(" "),
				typography: TYPOGRAPHY.body,
				segmentType,
				spacing: nextParagraphSpacing(),
				shading,
			})
		);
	}

	return blocks;
}

function groupDiffSegments(segments) {
	const groups = [];
	(segments || []).forEach((segment) => {
		const previous = groups[groups.length - 1];
		if (isTableRow(segment.text) && previous?.type === "table") {
			previous.segments.push(segment);
		} else if (isTableRow(segment.text)) {
			groups.push({ type: "table", segments: [segment] });
		} else {
			groups.push({ type: "block", segment });
		}
	});
	return groups;
}

function diffTableToDocx(tableSegments) {
	const rows = tableSegments.map((segment) => ({
		type: segment.type || "unchanged",
		cells: parseTableCells(segment.text),
	}));

	return new Table({
		width: { size: 100, type: WidthType.PERCENTAGE },
		rows: rows.map((row) => {
			const shading = segmentShading(row.type);
			return new TableRow({
				children: row.cells.map((cell) =>
					new TableCell({
						...(shading ? { shading } : {}),
						children: [
							buildParagraph({
								text: cell,
								segmentType: row.type,
								spacing: { after: 120, line: 384, lineRule: "auto" },
								shading,
							}),
						],
					})
				),
			});
		}),
	});
}

function diffSegmentsToDocxBlocks(segments, contentStyle) {
	const blocks = [];
	groupDiffSegments(segments).forEach((group, groupIndex) => {
		if (group.type === "table") {
			if (groupIndex > 0) {
				blocks.push(new Paragraph({ children: [makeRun("")], spacing: { after: 160 } }));
			}
			blocks.push(diffTableToDocx(group.segments));
			blocks.push(new Paragraph({ children: [makeRun("")], spacing: { after: 240 } }));
			return;
		}

		const segmentType = group.segment?.type || "unchanged";
		blocks.push(
			...markdownToDocxBlocks(group.segment?.text || "", {
				segmentType,
				contentStyle,
				blockSpacing:
					groupIndex > 0 ? { before: DIFF_BLOCK_GAP.before } : {},
			})
		);
	});
	return blocks;
}

function sectionHeading(label) {
	return new Paragraph({
		children: [makeRun(label.toUpperCase(), TYPOGRAPHY.sectionLabel)],
		spacing: SECTION_SPACING,
	});
}

function formatRunDate(value) {
	if (!value) return "";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
}

function modeLabel(mode) {
	return mode === "create" ? "Create new page" : "Optimize existing page";
}

function modeTypography(mode) {
	return mode === "create" ? TYPOGRAPHY.modeCreate : TYPOGRAPHY.modeOptimize;
}

function buildPageTitleSection(run) {
	const title =
		run?.recommendation_title || run?.recommendation_ref || `Run ${run?.id ?? ""}`;
	const blocks = [
		new Paragraph({
			children: [makeRun(modeLabel(run?.mode), modeTypography(run?.mode))],
			spacing: { after: 80 },
		}),
		new Paragraph({
			children: [makeRun(title, TYPOGRAPHY.pageTitle)],
			spacing: { after: 120, line: 384, lineRule: "auto" },
		}),
	];

	const targetUrl = run?.result?.target_url;
	if (targetUrl) {
		blocks.push(
			new Paragraph({
				children: [makeRun(String(targetUrl), { ...TYPOGRAPHY.body, underline: true })],
				spacing: { after: 80, line: 384, lineRule: "auto" },
			})
		);
	}

	const created = formatRunDate(run?.created_at);
	if (created) {
		blocks.push(
			new Paragraph({
				children: [makeRun(`Created ${created}`, TYPOGRAPHY.fieldHint)],
				spacing: { after: 80, ...METADATA_LINE },
			})
		);
	}

	return blocks;
}

function metadataFieldBlocks({ label, value, hint, currentValue }) {
	const blocks = [];
	const text = String(value ?? "").trim();
	const lineChildren = [makeRun(label, TYPOGRAPHY.fieldLabel)];
	if (text) {
		lineChildren.push(makeRun(" ", TYPOGRAPHY.body));
		text.split(/\n/).forEach((line, index) => {
			if (index > 0) lineChildren.push(new TextRun({ break: 1 }));
			lineChildren.push(makeRun(line, TYPOGRAPHY.body));
		});
	}
	blocks.push(
		new Paragraph({
			children: lineChildren,
			spacing: { after: hint ? METADATA_HINT_AFTER : currentValue != null ? METADATA_HINT_AFTER : METADATA_FIELD_GAP, ...METADATA_LINE },
		})
	);

	if (hint) {
		blocks.push(
			new Paragraph({
				children: [makeRun(hint, TYPOGRAPHY.fieldHint)],
				spacing: {
					after: currentValue != null ? METADATA_HINT_AFTER : METADATA_FIELD_GAP,
					...METADATA_LINE,
				},
			})
		);
	}

	if (currentValue != null) {
		blocks.push(
			new Paragraph({
				children: [
					makeRun("Current: ", TYPOGRAPHY.currentMeta),
					makeRun(String(currentValue), { ...TYPOGRAPHY.body, color: COLOR.muted }),
				],
				spacing: { after: METADATA_FIELD_GAP, ...METADATA_LINE },
			})
		);
	}

	return blocks;
}

function getMetadataFields(payload, mode, currentMetadata) {
	const showCurrent = mode === "optimize" && currentMetadata;
	const keywords = Array.isArray(payload.meta_keywords)
		? payload.meta_keywords.join(", ")
		: "";

	return [
		{
			label: "Meta title (SEO)",
			value: payload.meta_title,
			hint: `${String(payload.meta_title || "").length} / 60`,
			currentValue: showCurrent ? currentMetadata.title : null,
		},
		{
			label: "H1 heading",
			value: payload.h1,
			hint: "what visitors see on the page",
			currentValue: showCurrent ? currentMetadata.h1 : null,
		},
		...(mode === "create"
			? [{ label: "Suggested URL slug", value: payload.url_slug, hint: null, currentValue: null }]
			: []),
		{
			label: "Meta description",
			value: payload.meta_description,
			hint: `${String(payload.meta_description || "").length} / 160`,
			currentValue: showCurrent ? currentMetadata.description : null,
		},
		{
			label: "Meta keywords",
			value: keywords,
			hint: null,
			currentValue: showCurrent ? currentMetadata.keywords : null,
		},
	];
}

function buildMetadataBlocks(payload, mode, currentMetadata) {
	return getMetadataFields(payload, mode, currentMetadata).flatMap((field) =>
		metadataFieldBlocks(field)
	);
}

function buildMetadataSection(payload, mode, currentMetadata, run) {
	return [
		...buildPageTitleSection(run),
		sectionHeading("Page metadata"),
		...buildMetadataBlocks(payload, mode, currentMetadata),
		new Paragraph({
			children: [makeRun("")],
			spacing: FIELD_GROUP_SPACING,
		}),
	];
}

function buildPageContentBlocks({ mode, payload, segments }) {
	const hasDiff = mode === "optimize" && segments?.length > 0;
	const contentStyle = mode === "create" ? "create" : "optimize";
	const blocks = [sectionHeading("Page content")];

	if (hasDiff) {
		blocks.push(...diffSegmentsToDocxBlocks(segments, contentStyle));
		return blocks;
	}

	if (mode === "create") {
		const trimmedH1 = String(payload.h1 || "").trim();
		const bodyMarkdown = stripDuplicateLeadingH1(payload.body_markdown, trimmedH1);

		if (trimmedH1) {
			blocks.push(
				new Paragraph({
					children: [makeRun(trimmedH1, TYPOGRAPHY.createH1)],
					spacing: { after: 240, line: 384, lineRule: "auto" },
				})
			);
		}

		blocks.push(
			...markdownToDocxBlocks(bodyMarkdown, { contentStyle: "create" })
		);
		return blocks;
	}

	blocks.push(
		...markdownToDocxBlocks(payload.body_markdown, { contentStyle: "optimize" })
	);
	return blocks;
}

export function hasExportableWritingContent(payload, segments) {
	if (segments?.length) {
		return segments.some((segment) => {
			const text = stripImagesFromMarkdown(segment?.text || "");
			return Boolean(text.trim());
		});
	}
	if (!payload) return false;
	if (String(payload.h1 || "").trim()) return true;
	if (stripImagesFromMarkdown(payload.body_markdown).trim()) return true;
	return false;
}

const DOCX_MIME =
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function escapeHtml(value) {
	return String(value ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function resolveExportContext({ run, payload, content, segments }) {
	const mode = run?.mode || "optimize";
	const currentMetadata =
		mode === "optimize"
			? resolveCurrentMetadata(content, run?.result?.recommendation?.own_page)
			: null;
	return { mode, currentMetadata };
}

function buildWritingAssistantDocument({ run, payload, content, segments = null }) {
	const { mode, currentMetadata } = resolveExportContext({
		run,
		payload,
		content,
		segments,
	});

	const children = [
		...buildMetadataSection(payload, mode, currentMetadata, run),
		...buildPageContentBlocks({ mode, payload, segments }),
	];

	return new Document({
		numbering: {
			config: [
				{
					reference: "default-numbering",
					levels: [
						{
							level: 0,
							format: "decimal",
							text: "%1.",
							alignment: AlignmentType.START,
						},
					],
				},
			],
		},
		styles: {
			default: {
				document: {
					run: {
						font: UI_FONT,
						size: TYPOGRAPHY.body.size,
						color: COLOR.body,
					},
					paragraph: {
						spacing: BODY_PARAGRAPH_SPACING,
					},
				},
			},
		},
		sections: [{ children }],
	});
}

function pxFromHalfPt(size) {
	return Math.round((size / 2) * (96 / 72));
}

function htmlStyle(typography, segmentType = "unchanged") {
	const removed = segmentType === "removed";
	const parts = [
		`font-family:${UI_FONT},Arial,sans-serif`,
		`font-size:${pxFromHalfPt(typography.size)}px`,
		`color:#${removed ? COLOR.removed : typography.color}`,
		"line-height:1.6",
	];
	if (typography.bold) parts.push("font-weight:700");
	if (typography.italic) parts.push("font-style:italic");
	if (typography.underline) parts.push("text-decoration:underline");
	if (removed) parts.push("text-decoration:line-through");
	return parts.join(";");
}

function inlineMarkdownToHtml(text, typography = TYPOGRAPHY.body, segmentType = "unchanged") {
	const baseStyle = typography || TYPOGRAPHY.body;
	const value = String(text || "");
	if (!value) return "";

	const pattern =
		/(\*\*[^*]+\*\*|\*[^*]+\*|__[^_]+__|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
	let html = "";
	let lastIndex = 0;
	let match;

	while ((match = pattern.exec(value)) !== null) {
		if (match.index > lastIndex) {
			html += `<span style="${htmlStyle(baseStyle, segmentType)}">${escapeHtml(
				value.slice(lastIndex, match.index)
			)}</span>`;
		}
		const token = match[0];
		if (token.startsWith("**") && token.endsWith("**")) {
			html += `<strong style="${htmlStyle({ ...baseStyle, bold: true }, segmentType)}">${escapeHtml(token.slice(2, -2))}</strong>`;
		} else if (token.startsWith("*") && token.endsWith("*")) {
			html += `<em style="${htmlStyle({ ...baseStyle, italic: true }, segmentType)}">${escapeHtml(token.slice(1, -1))}</em>`;
		} else if (token.startsWith("__") && token.endsWith("__")) {
			html += `<strong style="${htmlStyle({ ...baseStyle, bold: true }, segmentType)}">${escapeHtml(token.slice(2, -2))}</strong>`;
		} else if (token.startsWith("_") && token.endsWith("_")) {
			html += `<em style="${htmlStyle({ ...baseStyle, italic: true }, segmentType)}">${escapeHtml(token.slice(1, -1))}</em>`;
		} else if (token.startsWith("`") && token.endsWith("`")) {
			html += `<code style="${htmlStyle(baseStyle, segmentType)}">${escapeHtml(token.slice(1, -1))}</code>`;
		} else if (token.startsWith("[")) {
			const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
			if (linkMatch) {
				html += `<a href="${escapeHtml(linkMatch[2])}" style="${htmlStyle({ ...baseStyle, underline: true }, segmentType)}">${escapeHtml(linkMatch[1])}</a>`;
			} else {
				html += escapeHtml(token);
			}
		} else {
			html += escapeHtml(token);
		}
		lastIndex = pattern.lastIndex;
	}

	if (lastIndex < value.length) {
		html += `<span style="${htmlStyle(baseStyle, segmentType)}">${escapeHtml(
			value.slice(lastIndex)
		)}</span>`;
	}

	return html;
}

function htmlParagraph(text, typography, segmentType, extraStyle = "") {
	return `<p style="margin:0 0 16px;${htmlStyle(typography, segmentType)}${extraStyle ? `;${extraStyle}` : ""}">${inlineMarkdownToHtml(text, typography, segmentType)}</p>`;
}

function segmentHtmlBackground(segmentType) {
	if (segmentType === "added") return `#${DIFF_ADDED_FILL}`;
	if (segmentType === "removed") return `#${DIFF_REMOVED_FILL}`;
	return "transparent";
}

function wrapSegmentHtml(innerHtml, segmentType) {
	if (segmentType === "unchanged") return innerHtml;
	return `<div style="background:${segmentHtmlBackground(segmentType)};padding:8px 10px;margin:0 0 8px;border-radius:4px;">${innerHtml}</div>`;
}

function markdownToHtml(markdown, options = {}) {
	const {
		segmentType = "unchanged",
		contentStyle = "optimize",
		blockSpacing = false,
	} = options;

	const cleaned = stripImagesFromMarkdown(markdown);
	const lines = cleaned.replace(/\r\n/g, "\n").split("\n");
	const parts = [];
	let index = 0;
	let isFirstBlock = true;

	while (index < lines.length) {
		const rawLine = lines[index];
		const line = rawLine.trim();

		if (!line || isImageOnlyLine(line)) {
			index += 1;
			continue;
		}

		const topGap =
			isFirstBlock && blockSpacing ? "margin-top:8px;" : isFirstBlock ? "" : "";
		if (isFirstBlock) isFirstBlock = false;

		if (isTableRow(rawLine)) {
			const tableLines = [];
			while (index < lines.length && isTableRow(lines[index])) {
				tableLines.push(lines[index]);
				index += 1;
			}
			const dataLines = tableLines.filter(
				(row) => !/^\|\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|$/.test(row.trim())
			);
			if (dataLines.length === 0) continue;

			const rows = dataLines
				.map(
					(row) =>
						`<tr>${parseTableCells(row)
							.map(
								(cell) =>
									`<td style="border:1px solid #${METADATA_BORDER_COLOR};padding:8px 10px;background:${segmentHtmlBackground(segmentType)};${htmlStyle(TYPOGRAPHY.body, segmentType)}">${inlineMarkdownToHtml(cell, TYPOGRAPHY.body, segmentType)}</td>`
							)
							.join("")}</tr>`
				)
				.join("");
			parts.push(
				`<table style="width:100%;border-collapse:collapse;margin:0 0 16px;${topGap}">${rows}</table>`
			);
			continue;
		}

		const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
		if (headingMatch) {
			const level = headingMatch[1].length;
			const headingType = headingTypography(level, contentStyle);
			const tag = level <= 1 ? "h1" : level === 2 ? "h2" : level === 3 ? "h3" : "h4";
			const marginTop = level <= 2 ? "margin-top:20px;" : "margin-top:16px;";
			parts.push(
				`<${tag} style="${marginTop}margin-bottom:8px;${htmlStyle(headingType, segmentType)}${topGap ? `;${topGap}` : ""}">${inlineMarkdownToHtml(headingMatch[2], headingType, segmentType)}</${tag}>`
			);
			index += 1;
			continue;
		}

		if (/^(-|\*|\+)\s+/.test(line)) {
			const items = [];
			while (index < lines.length && /^(-|\*|\+)\s+/.test(lines[index].trim())) {
				const item = lines[index].trim().replace(/^(-|\*|\+)\s+/, "");
				if (!isImageOnlyLine(item)) items.push(item);
				index += 1;
			}
			parts.push(
				`<ul style="margin:0 0 16px;padding-left:20px;${topGap}${htmlStyle(TYPOGRAPHY.listItem, segmentType)}">${items
					.map(
						(item) =>
							`<li style="margin:0 0 6px;">${inlineMarkdownToHtml(item, TYPOGRAPHY.listItem, segmentType)}</li>`
					)
					.join("")}</ul>`
			);
			continue;
		}

		if (/^\d+\.\s+/.test(line)) {
			const items = [];
			while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
				const item = lines[index].trim().replace(/^\d+\.\s+/, "");
				if (!isImageOnlyLine(item)) items.push(item);
				index += 1;
			}
			parts.push(
				`<ol style="margin:0 0 16px;padding-left:20px;${topGap}${htmlStyle(TYPOGRAPHY.listItem, segmentType)}">${items
					.map(
						(item) =>
							`<li style="margin:0 0 6px;">${inlineMarkdownToHtml(item, TYPOGRAPHY.listItem, segmentType)}</li>`
					)
					.join("")}</ol>`
			);
			continue;
		}

		if (line.startsWith(">")) {
			const quoteLines = [];
			while (index < lines.length && lines[index].trim().startsWith(">")) {
				quoteLines.push(lines[index].trim().replace(/^>\s?/, ""));
				index += 1;
			}
			parts.push(
				htmlParagraph(
					quoteLines.join(" "),
					{ ...TYPOGRAPHY.body, color: COLOR.muted },
					segmentType,
					`padding-left:12px;border-left:3px solid #${METADATA_BORDER_COLOR};${topGap}`
				)
			);
			continue;
		}

		const paragraphLines = [line];
		index += 1;
		while (
			index < lines.length &&
			lines[index].trim() &&
			!isImageOnlyLine(lines[index]) &&
			!isTableRow(lines[index]) &&
			!/^(#{1,6})\s+/.test(lines[index].trim()) &&
			!/^(-|\*|\+)\s+/.test(lines[index].trim()) &&
			!/^\d+\.\s+/.test(lines[index].trim()) &&
			!lines[index].trim().startsWith(">")
		) {
			paragraphLines.push(lines[index].trim());
			index += 1;
		}

		parts.push(
			htmlParagraph(paragraphLines.join(" "), TYPOGRAPHY.body, segmentType, topGap)
		);
	}

	return parts.join("");
}

function diffSegmentsToHtml(segments, contentStyle) {
	const parts = [];
	groupDiffSegments(segments).forEach((group, groupIndex) => {
		if (group.type === "table") {
			const rows = group.segments
				.map((segment) => {
					const type = segment.type || "unchanged";
					return `<tr style="background:${segmentHtmlBackground(type)};">${parseTableCells(segment.text)
						.map(
							(cell) =>
								`<td style="border:1px solid #${METADATA_BORDER_COLOR};padding:8px 10px;${htmlStyle(TYPOGRAPHY.body, type)}">${inlineMarkdownToHtml(cell, TYPOGRAPHY.body, type)}</td>`
						)
						.join("")}</tr>`;
				})
				.join("");
			parts.push(
				`<table style="width:100%;border-collapse:collapse;margin:${groupIndex > 0 ? "8px" : "0"} 0 16px;">${rows}</table>`
			);
			return;
		}

		const segmentType = group.segment?.type || "unchanged";
		const inner = markdownToHtml(group.segment?.text || "", {
			segmentType,
			contentStyle,
			blockSpacing: groupIndex > 0,
		});
		parts.push(wrapSegmentHtml(inner, segmentType));
	});
	return parts.join("");
}

function htmlExportParagraph(text, typography, marginBottom = "8pt", segmentType = "unchanged") {
	return `<p align="left" style="margin:0 0 ${marginBottom} 0;padding:0;text-align:left;mso-margin-top-alt:0;mso-margin-bottom-alt:auto;${htmlStyle(typography, segmentType)}">${text}</p>`;
}

function buildPageTitleHtml(run) {
	const title =
		run?.recommendation_title || run?.recommendation_ref || `Run ${run?.id ?? ""}`;
	const mode = run?.mode;
	let html = htmlExportParagraph(escapeHtml(modeLabel(mode)), modeTypography(mode), "4pt");
	html += htmlExportParagraph(escapeHtml(title), TYPOGRAPHY.pageTitle, "6pt");
	if (run?.result?.target_url) {
		html += htmlExportParagraph(
			escapeHtml(String(run.result.target_url)),
			{ ...TYPOGRAPHY.body, underline: true },
			"4pt"
		);
	}
	const created = formatRunDate(run?.created_at);
	if (created) {
		html += htmlExportParagraph(
			`Created ${escapeHtml(created)}`,
			TYPOGRAPHY.fieldHint,
			"12pt"
		);
	}
	return html;
}

function buildMetadataBlocksHtml(payload, mode, currentMetadata) {
	const labelStyle = htmlStyle(TYPOGRAPHY.fieldLabel);
	const hintStyle = htmlStyle(TYPOGRAPHY.fieldHint);
	const bodyStyle = htmlStyle(TYPOGRAPHY.body);
	const mutedStyle = htmlStyle({ ...TYPOGRAPHY.body, color: COLOR.muted });
	const currentStyle = htmlStyle(TYPOGRAPHY.currentMeta);

	return getMetadataFields(payload, mode, currentMetadata)
		.map(({ label, value, hint, currentValue }) => {
			const text = String(value ?? "").trim();
			const valueHtml = text
				? text
						.split(/\n/)
						.map((line) => inlineMarkdownToHtml(line, TYPOGRAPHY.body))
						.join("<br/>")
				: "";

			const hasFollowUp = Boolean(hint || currentValue != null);
			let html = `<p align="left" style="margin:0 0 ${hasFollowUp ? "2pt" : "12pt"} 0;padding:0;text-align:left;${bodyStyle}"><span style="${labelStyle}">${escapeHtml(label)}</span>${valueHtml ? ` ${valueHtml}` : ""}</p>`;
			if (hint) {
				html += htmlExportParagraph(
					escapeHtml(hint),
					TYPOGRAPHY.fieldHint,
					currentValue != null ? "2pt" : "12pt"
				);
			}
			if (currentValue != null) {
				html += `<p align="left" style="margin:0 0 12pt 0;padding:0;text-align:left;${bodyStyle}"><span style="${currentStyle}">Current:</span> <span style="${mutedStyle}">${escapeHtml(String(currentValue))}</span></p>`;
			}
			return html;
		})
		.join("");
}

function buildPageContentHtml({ mode, payload, segments }) {
	const hasDiff = mode === "optimize" && segments?.length > 0;
	const contentStyle = mode === "create" ? "create" : "optimize";
	let html = htmlExportParagraph("PAGE CONTENT", TYPOGRAPHY.sectionLabel, "8pt");

	if (hasDiff) {
		html += diffSegmentsToHtml(segments, contentStyle);
		return html;
	}

	if (mode === "create") {
		const trimmedH1 = String(payload.h1 || "").trim();
		const bodyMarkdown = stripDuplicateLeadingH1(payload.body_markdown, trimmedH1);
		if (trimmedH1) {
			html += `<h1 style="margin:0 0 16px;${htmlStyle(TYPOGRAPHY.createH1)}">${escapeHtml(trimmedH1)}</h1>`;
		}
		html += markdownToHtml(bodyMarkdown, { contentStyle: "create" });
		return html;
	}

	html += markdownToHtml(payload.body_markdown, { contentStyle: "optimize" });
	return html;
}

function buildDocumentPlainText({ run, payload, content, segments = null }) {
	const { mode, currentMetadata } = resolveExportContext({
		run,
		payload,
		content,
		segments,
	});
	const lines = [];
	const title =
		run?.recommendation_title || run?.recommendation_ref || `Run ${run?.id ?? ""}`;

	lines.push(modeLabel(run?.mode), title);
	if (run?.result?.target_url) lines.push(String(run.result.target_url));
	const created = formatRunDate(run?.created_at);
	if (created) lines.push(`Created ${created}`);
	lines.push("", "PAGE METADATA", "");

	getMetadataFields(payload, mode, currentMetadata).forEach(
		({ label, value, hint, currentValue }) => {
			const text = String(value ?? "").trim();
			lines.push(text ? `${label} ${text}` : label);
			if (hint) lines.push(hint);
			if (currentValue != null) lines.push(`Current: ${currentValue}`);
			lines.push("");
		}
	);

	lines.push("PAGE CONTENT", "");
	if (mode === "optimize" && segments?.length) {
		segments.forEach((segment) => {
			const text = stripImagesFromMarkdown(segment?.text || "").trim();
			if (text) lines.push(text);
		});
	} else if (mode === "create") {
		const trimmedH1 = String(payload.h1 || "").trim();
		if (trimmedH1) lines.push(trimmedH1);
		lines.push(stripImagesFromMarkdown(stripDuplicateLeadingH1(payload.body_markdown, trimmedH1)));
	} else {
		lines.push(stripImagesFromMarkdown(payload.body_markdown));
	}

	return lines.join("\n").trim();
}

function buildDocumentHtml({ run, payload, content, segments = null }) {
	const { mode, currentMetadata } = resolveExportContext({
		run,
		payload,
		content,
		segments,
	});

	const body = [
		buildPageTitleHtml(run),
		htmlExportParagraph("PAGE METADATA", TYPOGRAPHY.sectionLabel, "6pt"),
		buildMetadataBlocksHtml(payload, mode, currentMetadata),
		buildPageContentHtml({ mode, payload, segments }),
	].join("");

	return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<title>Writing Assistant Export</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View></w:WordDocument></xml><![endif]-->
</head>
<body lang="EN-US" style="margin:0;padding:0;text-align:left;font-family:${UI_FONT},Arial,sans-serif;font-size:12pt;color:#${COLOR.body};">
<div style="text-align:left;">${body}</div>
</body>
</html>`;
}

async function ensureDocxBlob(blob) {
	if (blob.type === DOCX_MIME) return blob;
	const buffer = await blob.arrayBuffer();
	return new Blob([buffer], { type: DOCX_MIME });
}

async function writeDocumentToClipboard(docxBlob, html, plainText) {
	if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
		throw new Error("Clipboard is not available in this browser.");
	}

	const htmlBlob = new Blob([html], { type: "text/html" });
	const plainBlob = new Blob([plainText], { type: "text/plain" });

	// Google Docs and most web editors paste from HTML — avoid bundling DOCX with HTML.
	try {
		await navigator.clipboard.write([
			new ClipboardItem({
				"text/html": htmlBlob,
				"text/plain": plainBlob,
			}),
		]);
		return;
	} catch {
		// Some browsers reject multi-format clipboard writes.
	}

	try {
		await navigator.clipboard.write([
			new ClipboardItem({
				[DOCX_MIME]: docxBlob,
			}),
		]);
		return;
	} catch {
		// Fall through to HTML-only clipboard.
	}

	await navigator.clipboard.write([
		new ClipboardItem({
			"text/html": htmlBlob,
		}),
	]);
}

export async function copyWritingAssistantDocument({
	run,
	payload,
	content,
	segments = null,
}) {
	if (!hasExportableWritingContent(payload, segments)) {
		throw new Error("Nothing to copy yet.");
	}

	const doc = buildWritingAssistantDocument({ run, payload, content, segments });
	const blob = await ensureDocxBlob(await Packer.toBlob(doc));
	const html = buildDocumentHtml({ run, payload, content, segments });
	const plainText = buildDocumentPlainText({ run, payload, content, segments });
	await writeDocumentToClipboard(blob, html, plainText);
}

export async function downloadWritingAssistantDocx({
	run,
	payload,
	content,
	segments = null,
}) {
	if (!hasExportableWritingContent(payload, segments)) {
		throw new Error("Nothing to download yet.");
	}

	const doc = buildWritingAssistantDocument({ run, payload, content, segments });
	const blob = await Packer.toBlob(doc);
	const filename = buildDocxFilename(run, payload);
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);

	return filename;
}
